import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import { Shield, Package, Receipt, Settings, Plus, Save, CheckCircle, AlertCircle, Mail, CreditCard, Inbox, MessageSquare, Trash2 } from "lucide-react";

const TABS = [
  { key: "packages", label: "Packages", icon: Package },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "requests", label: "Payment Requests", icon: Inbox },
  { key: "leads",    label: "Leads", icon: MessageSquare },
  { key: "platform", label: "Platform", icon: CreditCard },
  { key: "smtp",     label: "SMTP", icon: Mail },
  { key: "audit",    label: "Audit Log", icon: Shield },
];

export default function SuperAdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("packages");
  const [msg, setMsg] = useState(null);

  if (user && user.role !== "superadmin") return <Navigate to="/" replace />;

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-5 flex items-center gap-2">
          <Shield className="w-6 h-6 text-sage-600" />
          <h1 className="font-heading text-2xl font-semibold text-sage-900">Super Admin</h1>
        </div>
        {msg && <div className={`mb-4 flex items-center gap-2 text-sm rounded-md px-3 py-2 ${msg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{msg.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {msg.text}</div>}

        <div className="flex gap-2 mb-4 border-b border-sage-200">
          {TABS.map(t => (
            <button key={t.key} data-testid={`super-tab-${t.key}`} onClick={() => setTab(t.key)} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${tab === t.key ? "text-sage-900 border-b-2 border-sage-600 -mb-px" : "text-sage-500 hover:text-sage-800"}`}><t.icon className="w-4 h-4" /> {t.label}</button>
          ))}
        </div>

        {tab === "packages" && <PackagesTab flash={flash} />}
        {tab === "invoices" && <InvoicesTab flash={flash} />}
        {tab === "requests" && <RequestsTab flash={flash} />}
        {tab === "leads" && <LeadsTab flash={flash} />}
        {tab === "platform" && <PlatformTab flash={flash} />}
        {tab === "smtp" && <SmtpTab flash={flash} />}
        {tab === "audit" && <AuditTab />}
      </main>
    </div>
  );
}

function PackagesTab({ flash }) {
  const [pkgs, setPkgs] = useState([]);
  const [showMint, setShowMint] = useState(false);
  const [form, setForm] = useState({ user_email: "", tier: "starter", legacy: false, notes: "" });
  const [loading, setLoading] = useState(true);
  const [adjust, setAdjust] = useState(null);

  const load = () => { setLoading(true); api.get("/superadmin/packages").then(r => setPkgs(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const mint = async (e) => {
    e.preventDefault();
    try { await api.post("/superadmin/packages", form); setShowMint(false); flash("success", "Package minted"); load(); }
    catch (err) { flash("error", formatError(err)); }
  };

  const submitAdjust = async (patch) => {
    try { await api.put(`/superadmin/packages/${adjust.id}`, patch); flash("success", "Package updated"); setAdjust(null); load(); }
    catch (err) { flash("error", formatError(err)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-sage-500">{pkgs.length} package(s)</p>
        <button data-testid="mint-package-button" onClick={() => setShowMint(!showMint)} className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-sage-700"><Plus className="w-4 h-4" /> {showMint ? "Cancel" : "Mint Package"}</button>
      </div>
      {showMint && (
        <form onSubmit={mint} className="bg-white border border-[#E2E8E3] rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <input data-testid="mint-email" required type="email" placeholder="User email" value={form.user_email} onChange={e => setForm({ ...form, user_email: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm md:col-span-2" />
          <select data-testid="mint-tier" value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm bg-white">
            <option value="trial">Trial</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-sage-700"><input type="checkbox" checked={form.legacy} onChange={e => setForm({ ...form, legacy: e.target.checked })} /> Legacy (all tools, 10yr)</label>
          <button data-testid="mint-submit" type="submit" className="bg-sage-600 text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-sage-700">Mint</button>
        </form>
      )}

      {loading ? <p className="text-sage-500">Loading…</p> : (
        <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="super-packages-table">
            <thead className="bg-sage-50 text-xs uppercase tracking-wider text-sage-600">
              <tr><th className="text-left px-4 py-2">User</th><th className="text-left px-4 py-2">Tier</th><th className="text-left px-4 py-2">Slots</th><th className="text-left px-4 py-2">Tools</th><th className="text-left px-4 py-2">Expires</th><th className="text-left px-4 py-2">Status</th><th className="px-4 py-2"></th></tr>
            </thead>
            <tbody>
              {pkgs.map(p => (
                <tr key={p.id} className="border-t border-sage-100">
                  <td className="px-4 py-2">{p.user?.email}</td>
                  <td className="px-4 py-2 capitalize">{p.tier}</td>
                  <td className="px-4 py-2">{p.slots_used} / {p.slots_total}</td>
                  <td className="px-4 py-2">{p.tools_enabled.length}</td>
                  <td className="px-4 py-2 text-xs">{p.expires_at?.slice(0, 10)} {p.is_expired && <span className="text-red-600 ml-1">expired</span>}</td>
                  <td className="px-4 py-2 capitalize">{p.status}</td>
                  <td className="px-4 py-2 text-right"><button data-testid={`adjust-${p.id}`} onClick={() => setAdjust(p)} className="text-sage-600 text-xs font-semibold hover:underline">Adjust</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adjust && <AdjustPackageModal pkg={adjust} onClose={() => setAdjust(null)} onSubmit={submitAdjust} />}
    </div>
  );
}

function AdjustPackageModal({ pkg, onClose, onSubmit }) {
  const [slotsDelta, setSlotsDelta] = useState(0);
  const [extendDays, setExtendDays] = useState(0);
  const [status, setStatus] = useState(pkg.status);
  const [brandingAllowed, setBrandingAllowed] = useState(pkg.branding_allowed);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-md w-full p-5" data-testid="adjust-modal">
        <h3 className="font-heading text-lg font-semibold text-sage-900 mb-3">Adjust package · {pkg.user?.email}</h3>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1">Slots delta (+/-)</label>
            <input data-testid="adjust-slots-delta" type="number" value={slotsDelta} onChange={e => setSlotsDelta(Number(e.target.value))} className="w-full border border-sage-200 rounded-md px-3 py-2" />
            <p className="text-xs text-sage-400 mt-1">Current: {pkg.slots_used} used of {pkg.slots_total} total</p>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1">Extend expiry (days)</label>
            <input data-testid="adjust-extend-days" type="number" value={extendDays} onChange={e => setExtendDays(Number(e.target.value))} className="w-full border border-sage-200 rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1">Status</label>
            <select data-testid="adjust-status" value={status} onChange={e => setStatus(e.target.value)} className="w-full border border-sage-200 rounded-md px-3 py-2 bg-white">
              <option value="active">active</option><option value="suspended">suspended</option><option value="cancelled">cancelled</option>
            </select>
          </div>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="adjust-branding" checked={brandingAllowed} onChange={e => setBrandingAllowed(e.target.checked)} /> Branding allowed</label>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="border border-sage-200 rounded-md px-3 py-1.5 text-sm">Cancel</button>
          <button data-testid="adjust-submit" onClick={() => onSubmit({ slots_delta: slotsDelta || null, extend_days: extendDays || null, status: status !== pkg.status ? status : null, branding_allowed: brandingAllowed !== pkg.branding_allowed ? brandingAllowed : null })} className="bg-sage-600 text-white rounded-md px-4 py-1.5 text-sm font-semibold hover:bg-sage-700">Apply</button>
        </div>
      </div>
    </div>
  );
}

function InvoicesTab({ flash }) {
  const [invs, setInvs] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ user_email: "", amount_usd: 100, description: "Consulting package" });
  const load = () => api.get("/superadmin/invoices").then(r => setInvs(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/superadmin/invoices", form);
      flash("success", `Invoice created. ${res.data.email_sent ? "Email sent." : "SMTP not configured — manually share pay link."}`);
      setShow(false); load();
    } catch (err) { flash("error", formatError(err)); }
  };

  const markPaid = async (id) => {
    try { await api.put(`/superadmin/invoices/${id}/mark-paid`); flash("success", "Marked paid"); load(); }
    catch (err) { flash("error", formatError(err)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-sage-500">{invs.length} invoice(s)</p>
        <button data-testid="create-invoice-button" onClick={() => setShow(!show)} className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-sage-700"><Plus className="w-4 h-4" /> {show ? "Cancel" : "Create Invoice"}</button>
      </div>
      {show && (
        <form onSubmit={submit} className="bg-white border border-[#E2E8E3] rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input data-testid="inv-email" required type="email" placeholder="User email" value={form.user_email} onChange={e => setForm({ ...form, user_email: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm md:col-span-2" />
          <input data-testid="inv-amount" required type="number" step="0.01" min="1" placeholder="Amount USD" value={form.amount_usd} onChange={e => setForm({ ...form, amount_usd: Number(e.target.value) })} className="border border-sage-200 rounded-md px-3 py-2 text-sm" />
          <button data-testid="inv-submit" type="submit" className="bg-sage-600 text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-sage-700">Create & Email</button>
          <input data-testid="inv-desc" required type="text" placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="border border-sage-200 rounded-md px-3 py-2 text-sm md:col-span-4" />
        </form>
      )}
      <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="super-invoices-table">
          <thead className="bg-sage-50 text-xs uppercase tracking-wider text-sage-600"><tr><th className="text-left px-4 py-2">#</th><th className="text-left px-4 py-2">User</th><th className="text-left px-4 py-2">Amount</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Created</th><th className="px-4 py-2"></th></tr></thead>
          <tbody>
            {invs.map(i => (
              <tr key={i.id} className="border-t border-sage-100">
                <td className="px-4 py-2 font-mono text-xs">{i.number || i.id.slice(-8)}</td>
                <td className="px-4 py-2">{i.user_email}</td>
                <td className="px-4 py-2">USD {i.amount_usd.toFixed(2)}</td>
                <td className="px-4 py-2 capitalize">{i.status}</td>
                <td className="px-4 py-2 text-xs">{i.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-2 text-right space-x-2">
                  {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-sage-600 text-xs hover:underline">Pay link</a>}
                  {i.status !== "paid" && <button data-testid={`mark-paid-${i.id}`} onClick={() => markPaid(i.id)} className="text-xs text-green-700 hover:underline">Mark paid</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SmtpTab({ flash }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/superadmin/settings/smtp").then(r => setCfg(r.data)); }, []);
  if (!cfg) return <p className="text-sage-500">Loading…</p>;
  const save = async () => {
    try { await api.put("/superadmin/settings/smtp", cfg); flash("success", "SMTP saved"); }
    catch (e) { flash("error", formatError(e)); }
  };
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md p-5 max-w-xl" data-testid="smtp-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="block text-xs font-semibold text-sage-600 mb-1">Host</label><input data-testid="smtp-host" value={cfg.host || ""} onChange={e => setCfg({ ...cfg, host: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="smtp.gmail.com" /></div>
        <div><label className="block text-xs font-semibold text-sage-600 mb-1">Port</label><input data-testid="smtp-port" type="number" value={cfg.port || 587} onChange={e => setCfg({ ...cfg, port: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-semibold text-sage-600 mb-1">Username</label><input data-testid="smtp-user" value={cfg.user || ""} onChange={e => setCfg({ ...cfg, user: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-semibold text-sage-600 mb-1">Password</label><input data-testid="smtp-pass" type="password" value={cfg.password || ""} onChange={e => setCfg({ ...cfg, password: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="leave as •••••••• to keep" /></div>
        <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">From email</label><input data-testid="smtp-from" value={cfg.from_email || ""} onChange={e => setCfg({ ...cfg, from_email: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="billing@yourfirm.com" /></div>
        <label className="flex items-center gap-2 text-sm text-sage-700 md:col-span-2"><input type="checkbox" data-testid="smtp-tls" checked={cfg.use_tls !== false} onChange={e => setCfg({ ...cfg, use_tls: e.target.checked })} /> Use STARTTLS (port 587)</label>
      </div>
      <button data-testid="smtp-save" onClick={save} className="mt-4 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><Save className="w-4 h-4" /> Save SMTP</button>
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/superadmin/audit-log").then(r => setRows(r.data)); }, []);
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
      <table className="w-full text-sm" data-testid="super-audit-table">
        <thead className="bg-sage-50 text-xs uppercase tracking-wider text-sage-600"><tr><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Actor</th><th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Details</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t border-sage-100">
              <td className="px-4 py-2 text-xs">{new Date(r.ts).toLocaleString()}</td>
              <td className="px-4 py-2 text-xs font-mono">{r.actor}</td>
              <td className="px-4 py-2">{r.action}</td>
              <td className="px-4 py-2 text-xs text-sage-500 font-mono truncate max-w-md">{JSON.stringify(r.changes || r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function PlatformTab({ flash }) {
  const [cfg, setCfg] = useState({ stripe_enabled: true, billing_contact_email: "", manual_payment_instructions: "" });
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/superadmin/settings/platform").then(r => { setCfg(r.data); setLoading(false); }); }, []);
  const save = async () => {
    try { const r = await api.put("/superadmin/settings/platform", cfg); setCfg(r.data); flash("success", "Platform settings saved"); }
    catch (e) { flash("error", formatError(e)); }
  };
  if (loading) return <div className="text-sm text-sage-500">Loading…</div>;
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md p-5 max-w-xl" data-testid="platform-form">
      <h3 className="font-heading text-base font-semibold text-sage-900 mb-1">Platform Billing Settings</h3>
      <p className="text-xs text-sage-500 mb-4">Toggle Stripe online payments. When OFF, customers see a "Request invoice" button instead of "Buy" — requests appear under the <strong>Payment Requests</strong> tab.</p>
      <label className="flex items-center gap-3 mb-5 cursor-pointer">
        <input
          data-testid="platform-stripe-toggle"
          type="checkbox"
          checked={cfg.stripe_enabled !== false}
          onChange={(e) => setCfg({ ...cfg, stripe_enabled: e.target.checked })}
          className="w-4 h-4"
        />
        <div>
          <span className="text-sm font-semibold text-sage-900">Stripe online payments</span>
          <p className="text-xs text-sage-500">{cfg.stripe_enabled !== false ? "Customers can pay by card via Stripe Checkout." : "Online card payment is disabled. Customers must request an invoice."}</p>
        </div>
      </label>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-sage-600 mb-1">Billing contact email</label>
          <input
            data-testid="platform-billing-email"
            value={cfg.billing_contact_email || ""}
            onChange={(e) => setCfg({ ...cfg, billing_contact_email: e.target.value })}
            placeholder="billing@yourfirm.com"
            className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm"
          />
          <p className="text-xs text-sage-400 mt-1">Shown on the pricing page and in invoice request emails.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-sage-600 mb-1">Manual payment instructions</label>
          <textarea
            data-testid="platform-payment-instructions"
            value={cfg.manual_payment_instructions || ""}
            onChange={(e) => setCfg({ ...cfg, manual_payment_instructions: e.target.value })}
            rows={3}
            className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm"
            placeholder="Bank transfer details, finance contact, expected response time…"
          />
        </div>
      </div>
      <button data-testid="platform-save" onClick={save} className="mt-4 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2">
        <Save className="w-4 h-4" /> Save Platform Settings
      </button>
    </div>
  );
}

function RequestsTab({ flash }) {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/superadmin/purchase-requests").then(r => { setReqs(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);
  const resolve = async (id, status) => {
    try { await api.put(`/superadmin/purchase-requests/${id}/resolve`, { status }); flash("success", `Request ${status}`); load(); }
    catch (e) { flash("error", formatError(e)); }
  };
  if (loading) return <div className="text-sm text-sage-500">Loading…</div>;
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden" data-testid="purchase-requests-table">
      <div className="px-4 py-3 border-b border-sage-100 text-xs text-sage-500">Manual payment requests submitted by customers when Stripe is disabled or they prefer offline payment. Resolve by minting the package on the Packages tab + creating an invoice on the Invoices tab.</div>
      {reqs.length === 0 ? (
        <div className="text-center py-12 text-sage-500 text-sm">No payment requests yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-sage-50">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-sage-600 uppercase">Customer</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-sage-600 uppercase">Item</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-sage-600 uppercase">Amount</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-sage-600 uppercase">Status</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-sage-600 uppercase">Submitted</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {reqs.map((r) => (
              <tr key={r.id} className="border-t border-sage-100" data-testid={`request-row-${r.id}`}>
                <td className="px-3 py-2">
                  <p className="font-medium text-sage-900">{r.user_email}</p>
                  <p className="text-xs text-sage-500">{r.user_name}</p>
                </td>
                <td className="px-3 py-2"><span className="capitalize">{r.item_type}</span> · {r.item_key}</td>
                <td className="px-3 py-2 text-right">${(r.amount_usd || 0).toFixed(2)}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "pending" ? "bg-amber-100 text-amber-700" : r.status === "resolved" ? "bg-green-100 text-green-700" : "bg-sage-100 text-sage-600"}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-xs text-sage-500">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</td>
                <td className="px-3 py-2 text-right">
                  {r.status === "pending" && (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => resolve(r.id, "resolved")} data-testid={`resolve-${r.id}`} className="text-xs bg-sage-600 text-white rounded-md px-2 py-1 hover:bg-sage-700">Resolve</button>
                      <button onClick={() => resolve(r.id, "rejected")} data-testid={`reject-${r.id}`} className="text-xs border border-sage-200 text-sage-600 rounded-md px-2 py-1 hover:bg-sage-50">Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LeadsTab({ flash }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const load = async () => {
    const [l, s] = await Promise.all([
      api.get("/superadmin/leads" + (filter ? `?status=${filter}` : "")),
      api.get("/superadmin/leads/stats"),
    ]);
    setLeads(l.data); setStats(s.data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const update = async (id, status) => {
    try { await api.put(`/superadmin/leads/${id}`, { status }); flash("success", `Lead → ${status}`); load(); }
    catch (e) { flash("error", formatError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await api.delete(`/superadmin/leads/${id}`); flash("success", "Lead deleted"); load(); }
    catch (e) { flash("error", formatError(e)); }
  };
  if (loading) return <div className="text-sm text-sage-500">Loading…</div>;
  const STATUS_OPTS = ["new", "contacted", "qualified", "closed", "rejected"];
  const STATUS_COLOR = { new: "bg-amber-100 text-amber-700", contacted: "bg-sky-100 text-sky-700", qualified: "bg-emerald-100 text-emerald-700", closed: "bg-sage-100 text-sage-700", rejected: "bg-red-100 text-red-700" };
  return (
    <div className="space-y-4" data-testid="leads-tab">
      <div className="bg-white border border-[#E2E8E3] rounded-md p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-heading text-base font-semibold text-sage-900">Inbound leads</p>
            <p className="text-xs text-sage-500">Submissions from the public landing page form. {stats?.total || 0} total.</p>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="leads-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs border border-sage-200 rounded-md px-2 py-1.5">
              <option value="">All statuses</option>
              {STATUS_OPTS.map(s => <option key={s} value={s}>{s} ({stats?.by_status?.[s] || 0})</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
        {leads.length === 0 ? (
          <div className="text-center py-12 text-sage-500 text-sm">No leads yet. The contact form on the landing page feeds this list.</div>
        ) : (
          <ul className="divide-y divide-sage-100">
            {leads.map((l) => (
              <li key={l.id} className="p-4" data-testid={`lead-row-${l.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sage-900 text-sm">{l.name || l.email}</p>
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${STATUS_COLOR[l.status] || "bg-sage-100 text-sage-600"}`}>{l.status}</span>
                      {l.company && <span className="text-xs text-sage-500">· {l.company}</span>}
                    </div>
                    <p className="text-xs text-sage-500 mt-0.5">
                      <a href={`mailto:${l.email}`} className="underline">{l.email}</a>
                      {l.created_at ? ` · ${new Date(l.created_at).toLocaleString()}` : ""}
                      {l.source ? ` · ${l.source}` : ""}
                    </p>
                    {l.problem && <p className="text-sm text-sage-700 mt-2 italic">"{l.problem}"</p>}
                    {l.utm && Object.keys(l.utm).length > 0 && (
                      <p className="text-[10px] text-sage-400 mt-1 font-mono">{Object.entries(l.utm).slice(0, 4).map(([k, v]) => `${k}=${v}`).join(" · ")}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => update(l.id, e.target.value)} className="text-xs border border-sage-200 rounded-md px-2 py-1">
                      {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button data-testid={`lead-delete-${l.id}`} onClick={() => remove(l.id)} title="Delete lead" className="text-red-500 hover:text-red-700 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
